import { trackEvent } from "@/lib/analytics";
import GooglePlayBadge from "@/components/GooglePlayBadge";
import { useT } from "@/i18n/LanguageContext";

/**
 * Primary store badges — used on the homepage final CTA, all sub-landings,
 * and the FAQ page.
 *
 *   • Apple App Store — official "Download on the App Store" badge per
 *     Apple's marketing guidelines. Localised between EN and NL via the
 *     bundled SVGs in /public/badges/. Opens the App Store in a new tab.
 *
 *   • Google Play     — official "Get it on Google Play" badge, localised
 *     per language. Opens the Play Store listing in a new tab.
 *
 * Both badges are rendered at the same visible height for equal prominence.
 */

const APPSTORE_URL = process.env.REACT_APP_APPSTORE_URL || "";

const APPLE_BADGE_HEIGHT_PX = 54; // honour Apple's minimum + guideline ratio
const APPLE_BADGE_WIDTH_PX = Math.round(APPLE_BADGE_HEIGHT_PX * (119.66407 / 40));

export default function StoreBadges({ align = "start" }) {
  const { lang } = useT();

  const onAppleClick = () => {
    trackEvent("app_store_click", {
      placement: "store_badges_appstore",
      has_store_url: !!APPSTORE_URL,
    });
  };

  const appleBadgeSrc = lang === "nl" ? "/badges/app-store-nl.svg" : "/badges/app-store-en.svg";

  return (
    <div
      id="download"
      data-testid="store-badges"
      className={`flex flex-col sm:flex-row items-center gap-4 ${
        align === "center" ? "justify-center" : ""
      }`}
    >
      {/* APPLE — official badge, unmodified */}
      <a
        href={APPSTORE_URL || "#"}
        target={APPSTORE_URL ? "_blank" : undefined}
        rel={APPSTORE_URL ? "noopener noreferrer" : undefined}
        data-testid="appstore-cta"
        onClick={onAppleClick}
        aria-label="Download Virtue Guide on the App Store"
        className="inline-block transition-transform duration-300 hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
        style={{ lineHeight: 0 }}
      >
        <img
          src={appleBadgeSrc}
          alt="Download on the App Store"
          width={APPLE_BADGE_WIDTH_PX}
          height={APPLE_BADGE_HEIGHT_PX}
          style={{ height: `${APPLE_BADGE_HEIGHT_PX}px`, width: "auto", display: "block" }}
          draggable={false}
        />
      </a>

      {/* GOOGLE PLAY — official badge, active */}
      <GooglePlayBadge
        visibleHeight={APPLE_BADGE_HEIGHT_PX}
        placement="store_badges_playstore"
        testid="playstore-cta"
      />
    </div>
  );
}
